import React, { useContext, useState, useEffect, createContext } from 'react';
import * as R from 'ramda';
import { token } from '@atlaskit/tokens';
import colors from '@atlassian/jira-portfolio-3-common/src/colors';
import { Container } from './container';
import withNamespacedId from './namespaced-id';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import TitleWithIcon from './title-with-icon';
import type { ColumnTextProps, ColumnTextState, ColumnTextsBySectionsId } from './types';

const Context = createContext<{
	value: ColumnTextsBySectionsId;
	update: (id: string, arg2: ColumnTextState[]) => void;
}>({
	value: {},
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	update: () => {},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function Provider(props: Record<string, any>) {
	const [value, setValue] = useState<ColumnTextsBySectionsId>({});
	const update = (id: string, columnTexts: ColumnTextState[]) => {
		// eslint-disable-next-line @typescript-eslint/no-shadow
		setValue((value) => ({ ...value, [id]: columnTexts }));
	};
	return <Context.Provider {...props} value={{ value, update }} />;
}

export function Updater({ id, columnTexts }: { id: string; columnTexts: ColumnTextState[] }) {
	const { value, update } = useContext(Context);
	useEffect(() => {
		const currColumnTexts = value[id];

		if (!R.equals(columnTexts, currColumnTexts)) {
			update(id, columnTexts);
		}
	}, [columnTexts, id, update, value]);
	return null;
}

function ColumnText({
	icon,
	isExportMode,
	title = '',
	tooltip,
	visible,

	// as far as I could test, width is always defined but Flow thinks otherwise, so I set it to 24px by default
	width = 24,
}: ColumnTextProps) {
	/**
	 * When a field is collapsed in the Fields section of the roadmap, we rotate the name in order to render it vertically.
	 * However, this is not supported well by html2canvas which crops the text after a few pixels, so in export mode, we use a svg to render the field name.
	 */
	if (isExportMode) {
		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.columnText} style={{ width }}>
				{visible && (
					<svg>
						<text
							fill={token('color.blanket', colors.N200)}
							fontFamily="sans-serif"
							fontSize="10px"
							fontWeight="bold"
							/**
							 * Collapsed columns are either:
							 * - 32px wide (when all fields are collapsed and "FIELDS" is the only column)
							 * - 24px wide (for a collapsed field)
							 * therefore we use the width to move the text horizontally and center it into its column
							 */
							transform={`translate(${((width || 24) - 8) / 2}, 22) rotate(90)`}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ textTransform: 'uppercase' }}
						>
							<tspan>{title} </tspan>
							{/**
							 * we cannot set a background colour because we're in a SVG, we could use
							 * <rect fill="<color>" /> followed by a <text />, but then we need to know
							 * how much the previous <text /> (field name) is going to expand in order to position
							 * the <rect /> accordingly, and all this happens in a container where dimensions are inverted.
							 * For the purpose of the MVP, we'll just go with [D] but there is definitely room for improvement here.
							 * */}
							{icon !== undefined ? <tspan>[D]</tspan> : null}
						</text>
					</svg>
				)}
			</div>
		);
	}
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.columnText} style={{ width }}>
			{visible && (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<div className={styles.columnTextTitle}>
					<TitleWithIcon icon={icon} tooltip={tooltip} mode="collapsed">
						{title}
					</TitleWithIcon>
				</div>
			)}
		</div>
	);
}

function ColumnTexts({
	id,
	isExportMode = false,
	...otherProps
}: {
	id: string;
	isExportMode: boolean;
}) {
	const columnTexts = useContext(Context).value[id];

	if (!columnTexts) {
		return null;
	}

	return (
		// @ts-expect-error - TS2322 - Type '{ children: Element[]; id: string; styleName: string; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<Omit<SectionContainerProps, "width" | "dispatch"> & Props, any, any>> & Readonly<...> & Readonly<...>'.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<Container {...otherProps} id={id} className={styles.columnTexts}>
			{columnTexts.map((columnText, idx) => (
				<ColumnText key={idx} isExportMode={isExportMode} {...columnText} />
			))}
		</Container>
	);
}

export default withNamespacedId(ColumnTexts);
